import type { ScormMetadataRow } from "services/KnowfullyAdminClient";
import * as XLSX from "xlsx";

export const METADATA_FILE_NAME = "metadata.xlsx";
export const METADATA_SHEET_NAME = "Sheet1";

const DATE_FORMAT = "mm/dd/yyyy";
const DATE_TIME_FORMAT = "mm/dd/yyyy h:mm:ss AM/PM";

type CellValue = string | number | Date | undefined;

type MetadataColumn = {
  header: string;
  value: (row: ScormMetadataRow) => CellValue;
  dateFormat?: string;
};

// The delivery console reads column A by position and finds "SCORM File Name" by header, and vendors import by
// header text, so neither the order nor the wording may change.
export const METADATA_COLUMNS: MetadataColumn[] = [
  { header: "Content Provider Unique ID", value: (row) => row.contentProviderUniqueId },
  { header: "Course Title", value: (row) => row.courseTitle },
  { header: "Field Of Study", value: (row) => row.fieldOfStudy },
  { header: "Course Description", value: (row) => row.courseDescription },
  { header: "Learning Objectives", value: (row) => row.learningObjectives },
  { header: "Publication Revision Date", value: (row) => row.publicationRevisionDate, dateFormat: DATE_FORMAT },
  { header: "Session Start Date Time", value: (row) => row.sessionStartDateTime, dateFormat: DATE_TIME_FORMAT },
  { header: "Session End Date Time", value: (row) => row.sessionEndDateTime, dateFormat: DATE_TIME_FORMAT },
  { header: "Enrollment End Date Time", value: (row) => row.enrollmentEndDateTime, dateFormat: DATE_TIME_FORMAT },
  { header: "Timezone", value: (row) => row.timezone },
  { header: "Length", value: (row) => row.length },
  { header: "CPE Credits", value: (row) => row.cpeCredits },
  { header: "Instructor", value: (row) => row.instructor },
  { header: "Course Level", value: (row) => row.courseLevel },
  { header: "Prerequisites", value: (row) => row.prerequisites },
  { header: "Advanced Preparation", value: (row) => row.advancedPreparation },
  { header: "Sponsor Name", value: (row) => row.sponsorName },
  { header: "Program Jurisdictions", value: (row) => row.programJurisdictions },
  { header: "Program Jurisdiction Ids", value: (row) => row.programJurisdictionIds },
  { header: "Delivery Method", value: (row) => row.deliveryMethod },
  { header: "SCORM File Name", value: (row) => row.scormFileName },
  { header: "Surgent NASBA Sponsor ID", value: (row) => row.surgentNasbaSponsorId },
  { header: "Qualifications", value: (row) => row.qualifications },
  { header: "IRS Credit Start Date", value: (row) => row.irsCreditStartDate, dateFormat: DATE_FORMAT },
  { header: "IRS Credit End Date", value: (row) => row.irsCreditEndDate, dateFormat: DATE_FORMAT },
  { header: "Image URL", value: (row) => row.imageUrl },
  { header: "Price", value: (row) => row.price },
  { header: "Course Version", value: (row) => row.courseVersion },
];

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
const EXCEL_EPOCH = Date.UTC(1899, 11, 30);
const FIRST_SERIAL_AFTER_EXCEL_LEAP_DAY = 61;

// Built from the wall-clock fields rather than left to SheetJS: its own conversion subtracts the time zone offset
// of 1899, which in zones that still used local mean time then (Madrid, for one) turns 9:00 AM into 8:59 AM.
// Excel counts a 29 February 1900 that never existed, so the 1899-12-30 epoch is only right from 1 March 1900
// on; earlier dates are one day lower (1900-01-01 is serial 1). Self-study rows carry 1900-01-01 as their
// Enrollment End, so this is not a theoretical case.
export const toExcelSerial = (date: Date): number => {
  const serial =
    (Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    ) -
      EXCEL_EPOCH) /
    MILLISECONDS_PER_DAY;

  return serial < FIRST_SERIAL_AFTER_EXCEL_LEAP_DAY ? serial - 1 : serial;
};

const toCellValue = (value: CellValue): string | number => {
  if (value === undefined || value === null) {
    return "";
  }

  return value instanceof Date ? toExcelSerial(value) : value;
};

// Dates arrive as Date objects from the generated client and are written as Excel serial numbers with a number
// format per column, so they open as real dates and the session columns keep their time.
export const buildMetadataWorkbook = (rows: ScormMetadataRow[]): XLSX.WorkBook => {
  const header = METADATA_COLUMNS.map((column) => column.header);
  const body = rows.map((row) =>
    METADATA_COLUMNS.map((column) => toCellValue(column.value(row))),
  );

  const sheet = XLSX.utils.aoa_to_sheet([header, ...body]);

  METADATA_COLUMNS.forEach((column, columnIndex) => {
    if (!column.dateFormat) {
      return;
    }

    rows.forEach((_, rowIndex) => {
      const cell = sheet[XLSX.utils.encode_cell({ r: rowIndex + 1, c: columnIndex })];

      if (cell && cell.t === "n") {
        cell.z = column.dateFormat;
      }
    });
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, METADATA_SHEET_NAME);

  return workbook;
};
